import { useEffect, useRef, useState } from 'react';
import { Button, Card, Input, useToast, v } from '@kss/ui';
import { Loader2, MapPin, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '@kss/auth';
import { lookupPincode } from '../utils/pincodeLookup';

interface Address {
  id: string;
  label: string;
  line1: string;
  line2?: string;
  city: string;
  state: string;
  pincode: string;
  phone: string;
  isDefault?: boolean;
}

const STORAGE_PREFIX = 'kss.addresses.v1';

function storageKey(userId?: string | null): string {
  return `${STORAGE_PREFIX}:${userId ?? 'guest'}`;
}

function readAddresses(userId?: string | null): Address[] {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (raw) return JSON.parse(raw) as Address[];
  } catch {
    /* malformed JSON — start clean */
  }
  return [];
}

const EMPTY_DRAFT: Omit<Address, 'id'> = {
  label: '',
  line1: '',
  city: '',
  state: '',
  pincode: '',
  phone: '',
};

/**
 * Manages shipping/billing addresses for the signed-in customer.
 *
 * Storage: per-user `localStorage` slot at `kss.addresses.v1:<userId>`,
 * mirroring the cart provider's per-user shape so logging out doesn't
 * leak addresses to a guest. Once the backend exposes `addressService`
 * the read/write helpers swap for `addressService.filter({ customerId })`
 * and the local cache becomes a fallback.
 *
 * UX: pincode → city/state auto-fill on the add form (same
 * `lookupPincode` helper the checkout uses), so the customer types
 * the 6-digit pin and the rest fills itself.
 */
export default function AccountAddresses() {
  const toast = useToast();
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const [items, setItems] = useState<Address[]>(() => readAddresses(userId));
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<Omit<Address, 'id'>>(EMPTY_DRAFT);
  const [resolvingPin, setResolvingPin] = useState(false);
  const [draftTouched, setDraftTouched] = useState(false);

  /**
   * Field errors for the add-address draft.
   *
   * Text fields carry the SQL + XSS guards because these values are
   * echoed onto the printed invoice's shipping block, where React's
   * default escaping no longer protects them.
   */
  const draftErrors: Record<string, string | null> = {
    label: v.runRules(draft.label, [v.required('Label'), v.maxLength(40, 'Label'), ...v.safeText('Label')]),
    line1: v.runRules(draft.line1, [v.required('Line 1'), v.maxLength(200, 'Line 1'), ...v.safeText('Line 1')]),
    line2: v.runRules(draft.line2 ?? '', [v.maxLength(200, 'Line 2'), ...v.safeText('Line 2')]),
    city: v.runRules(draft.city, [v.required('City'), v.maxLength(100, 'City'), ...v.safeText('City')]),
    state: v.runRules(draft.state ?? '', [v.maxLength(100, 'State'), ...v.safeText('State')]),
    // Indian PIN: exactly 6 digits and never leading-zero.
    pincode: v.runRules(draft.pincode, [
      v.required('Pincode'),
      v.numericOnly('Pincode'),
      v.minLength(6, 'Pincode'),
      v.maxLength(6, 'Pincode'),
    ]),
    // Optional, but must be a real number when supplied.
    phone: v.runRules(draft.phone ?? '', [v.mobile({ label: 'Phone' })]),
  };
  const draftErr = (k: string) => (draftTouched ? draftErrors[k] ?? undefined : undefined);
  const lastResolvedPin = useRef('');
  const lastUserId = useRef<string | null>(userId);

  // Re-read from localStorage when the signed-in user switches —
  // mirrors `CartProvider`'s sign-in/sign-out swap so addresses
  // never bleed across accounts.
  useEffect(() => {
    if (lastUserId.current === userId) return;
    lastUserId.current = userId;
    setItems(readAddresses(userId));
  }, [userId]);

  // Persist whenever the list changes.
  useEffect(() => {
    try {
      localStorage.setItem(storageKey(userId), JSON.stringify(items));
    } catch {
      /* storage full / disabled — surface silently */
    }
  }, [items, userId]);

  // Pincode auto-fill on the add form. Same 500ms debounce + dedup
  // shape as the checkout. Quietly no-ops if the lookup fails so the
  // user can still type city/state by hand.
  useEffect(() => {
    if (!adding) return;
    const pin = (draft.pincode ?? '').trim();
    if (!/^\d{6}$/.test(pin)) return;
    if (lastResolvedPin.current === pin) return;
    const t = setTimeout(async () => {
      setResolvingPin(true);
      try {
        const res = await lookupPincode(pin);
        if (!res) return;
        lastResolvedPin.current = pin;
        setDraft((p) => ({
          ...p,
          city: p.city || res.city,
          state: p.state || res.state,
        }));
      } finally {
        setResolvingPin(false);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [draft.pincode, adding]);

  function startAdd() {
    setAdding(true);
    setDraft(EMPTY_DRAFT);
    lastResolvedPin.current = '';
  }
  function cancelAdd() {
    setAdding(false);
  }
  function saveAdd(e: React.FormEvent) {
    e.preventDefault();
    setDraftTouched(true);
    /* Was presence-only on four fields, with no format checks at all —
       a 3-digit pincode, a 2-digit phone or a script tag in the label
       all saved cleanly and then shipped to checkout. */
    const firstError = Object.values(draftErrors).find(Boolean);
    if (firstError) {
      toast.error('Address incomplete', firstError);
      return;
    }
    const id = 'addr_' + Math.random().toString(36).slice(2, 8);
    // First address auto-promotes to default so checkout has a sane
    // shipping target. Subsequent additions stay non-default unless
    // the user explicitly promotes them.
    const isDefault = items.length === 0;
    setItems((p) => [...p, { ...draft, id, isDefault }]);
    setAdding(false);
    toast.success('Address saved', `${draft.label} added.`);
  }
  function makeDefault(id: string) {
    setItems((p) => p.map((a) => ({ ...a, isDefault: a.id === id })));
  }
  function removeOne(id: string) {
    setItems((p) => {
      const next = p.filter((a) => a.id !== id);
      // If we just removed the default, promote the first remaining
      // entry so the customer always has a default to ship to.
      const removed = p.find((a) => a.id === id);
      if (removed?.isDefault && next.length > 0) {
        next[0] = { ...next[0]!, isDefault: true };
      }
      return next;
    });
  }

  return (
    <div className="grid gap-3">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-[var(--color-text-primary)]">Addresses</h1>
        {!adding && (
          <Button size="sm" onClick={startAdd}>
            <Plus className="h-3.5 w-3.5" />
            Add address
          </Button>
        )}
      </div>

      {items.length === 0 && !adding && (
        <Card className="grid place-items-center gap-2 p-10 text-center">
          <MapPin className="h-8 w-8 text-[var(--color-text-muted)]" />
          <p className="text-sm font-medium text-[var(--color-text-primary)]">
            No saved addresses yet
          </p>
          <p className="max-w-sm text-xs text-[var(--color-text-secondary)]">
            Save your shipping addresses here for faster checkout.
          </p>
          <Button size="sm" className="mt-2" onClick={startAdd}>
            <Plus className="h-3.5 w-3.5" />
            Add your first address
          </Button>
        </Card>
      )}

      {items.map((a) => (
        <Card key={a.id} className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                {a.label}
                {a.isDefault && (
                  <span className="ml-2 rounded bg-[var(--color-primary)]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[var(--color-primary)]">
                    Default
                  </span>
                )}
              </p>
              <p className="mt-1 text-sm text-[var(--color-text-primary)]">
                {a.line1}{a.line2 ? `, ${a.line2}` : ''}
              </p>
              <p className="text-sm text-[var(--color-text-secondary)]">
                {a.city}, {a.state} {a.pincode}
              </p>
              <p className="mt-1 text-xs text-[var(--color-text-muted)]">{a.phone}</p>
            </div>
            <div className="flex items-center gap-2">
              {!a.isDefault && (
                <Button size="sm" variant="outline" onClick={() => makeDefault(a.id)}>
                  Make default
                </Button>
              )}
              <button
                type="button"
                onClick={() => removeOne(a.id)}
                aria-label="Remove address"
                className="inline-flex h-8 w-8 items-center justify-center rounded text-[var(--color-text-muted)] hover:text-[var(--color-danger)]"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        </Card>
      ))}

      {adding && (
        <Card className="p-5">
          <form onSubmit={saveAdd} className="grid gap-3 sm:grid-cols-2">
            <Input label="Label" placeholder="Home / Office" value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} onBlur={() => setDraftTouched(true)} error={draftErr("label")} />
            <Input
              label="Phone"
              placeholder="10-digit mobile"
              value={draft.phone}
              /* Strip non-digits at the input boundary — same convention
                 as CheckoutPage. Pasting "+91 98765 43210" normalises
                 to "919876543210". Bug ref: AI Agent Issue #2 (applied
                 to address book for consistency). */
              onChange={(e) =>
                setDraft({ ...draft, phone: e.target.value.replace(/\D+/g, '').slice(0, 15) })
              }
              inputMode="numeric"
              pattern="\d{10,15}"
              maxLength={15}
              onBlur={() => setDraftTouched(true)}
              // Was a bare length check that passed "1111111111"; the
              // shared rule also enforces the 6-9 leading digit.
              error={draftErr('phone')}
            />
            <div className="sm:col-span-2">
              <Input label="Line 1" placeholder="House / building / street" value={draft.line1} onChange={(e) => setDraft({ ...draft, line1: e.target.value })} onBlur={() => setDraftTouched(true)} error={draftErr("line1")} />
            </div>
            <div className="sm:col-span-2">
              <Input label="Line 2 (optional)" placeholder="Landmark / area" value={draft.line2 ?? ""} onChange={(e) => setDraft({ ...draft, line2: e.target.value })} error={draftErr("line2")} />
            </div>
            <label className="flex flex-col gap-1">
              <span className="flex items-center gap-1 text-xs font-medium text-[var(--color-text-secondary)]">
                Pincode
                {resolvingPin && (
                  <Loader2 className="h-3 w-3 animate-spin text-[var(--color-text-muted)]" />
                )}
              </span>
              <input
                inputMode="numeric"
                pattern="\d*"
                value={draft.pincode}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    pincode: e.target.value.replace(/\D/g, '').slice(0, 6),
                  })
                }
                onBlur={() => setDraftTouched(true)}
                placeholder="400050"
                maxLength={6}
                className={`h-10 w-full rounded-md border bg-[var(--color-bg-card)] px-3 font-mono text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-primary)] ${
                  draftErr('pincode')
                    ? 'border-[var(--color-danger)]'
                    : 'border-[var(--color-border)]'
                }`}
              />
              {draftErr('pincode') && (
                <span className="text-xs text-[var(--color-danger)]">{draftErr('pincode')}</span>
              )}
            </label>
            <Input label="City" value={draft.city} onChange={(e) => setDraft({ ...draft, city: e.target.value })} onBlur={() => setDraftTouched(true)} error={draftErr("city")} />
            <Input label="State" value={draft.state} onChange={(e) => setDraft({ ...draft, state: e.target.value })} onBlur={() => setDraftTouched(true)} error={draftErr("state")} />
            <p className="sm:col-span-2 -mt-1 text-[11px] text-[var(--color-text-muted)]">
              Tip: type your 6-digit pincode first — we’ll auto-fill city &amp; state.
            </p>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={cancelAdd}>Cancel</Button>
              <Button type="submit">Save address</Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}
